import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ID = "db8ip88kk00l6eltrklg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db8ip88kk00l6eltrklg";

const contactInfo = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@kuangwang-movie.demo" },
  { icon: "ri-phone-line", label: "联系电话", value: "400-000-2026（工作日 9:00-18:00）" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "示例市 影视大道 88 号 文创大厦 12 层" },
  { icon: "ri-time-line", label: "处理时效", value: "收到反馈后 3 个工作日内回复" },
];

export default function Contact() {
  usePageMeta(
    "联系我们 - 疯狂的欲望影视资讯站",
    "联系疯狂的欲望：媒体合作、内容纠错与用户反馈，欢迎通过邮箱或站内表单与我们联系。",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的反馈，我们已收到并会尽快处理。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const v = String(value).trim();
      if (v) payload.append(key, v);
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("感谢你的反馈，我们已收到并会尽快处理。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试，或直接发送邮件给我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请检查网络后重试，或直接发送邮件给我们。");
    }
  };

  return (
    <div className="w-full">
      <section className="bg-primary-950 text-background-50">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
          <nav className="mb-5 flex items-center gap-2 text-xs text-background-300">
            <Link to="/" className="cursor-pointer hover:text-background-50">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">联系我们</span>
          </nav>
          <h1 className="font-heading text-3xl font-black md:text-5xl">联系我们</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            无论是内容纠错、媒体合作，还是对网站的改进建议，都欢迎告诉我们。每一条反馈我们都会认真阅读。
          </p>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
          <div className="mt-6 space-y-4">
            {contactInfo.map((c) => (
              <div key={c.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${c.icon} text-xl`} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">{c.label}</p>
                  <p className="mt-1 text-sm text-foreground-900">{c.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-lg bg-secondary-100 p-5 text-sm leading-relaxed text-secondary-900">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold">
              <i className="ri-information-line" />
              反馈处理机制
            </h3>
            <p className="mt-2">
              表单提交后我们会收到通知，并在 3 个工作日内通过你填写的邮箱回复。若涉及内容纠错，我们会在核实后更新相关页面。
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">在线反馈</h2>
            <p className="mt-2 text-sm text-foreground-600">请填写以下信息，带 * 的为必填项。</p>

            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-website-alt"
              />

              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50"
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50"
                />
              </div>

              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  反馈类型
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:bg-background-50"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="媒体合作">媒体合作</option>
                  <option value="其他反馈">其他反馈</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  反馈内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的建议或问题（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50"
                />
              </div>

              {status === "success" && (
                <div className="flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line mt-0.5" />
                  <span>{message}</span>
                </div>
              )}
              {status === "error" && (
                <div className="flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                  <i className="ri-error-warning-line mt-0.5" />
                  <span>{message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}